import React, { useState, useEffect } from 'react';
import blockchainService from '../../services/blockchainService';
import {
  Coins,
  Lock,
  Unlock,
  Flame,
  Plus,
  Minus,
  TrendingDown,
  Fuel,
  Settings,
  Users,
  Send,
  Download,
  Upload,
  Calendar,
  Clock,
  Shield,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Eye,
  Edit,
  Trash2,
  Save,
  X,
  Info,
  BarChart3,
  PieChart,
  Activity,
  Wallet,
  FileText,
  Target,
  Zap,
  RefreshCw,
  Database,
  Key,
  Globe,
  DollarSign,
  Star
} from 'lucide-react';
import { VTTN_BURNED, VTTN_CIRCULATING, VTTN_HOLDERS, VTTN_LOCKED, VTTN_PRICE_USD, VTTN_TOTAL_SUPPLY } from '../../constants/vttn';

// 타입 정의
interface TokenStats {
  totalSupply: string;
  circulatingSupply: string;
  burnedAmount: string;
  lockedAmount: string;
  vestingAmount: string;
  currentPrice: number;
  marketCap: number;
  holders: number;
}

interface VestingSchedule {
  id: string;
  beneficiary: string;
  totalAmount: string;
  releasedAmount: string;
  startDate: string;
  duration: number; // months
  cliffPeriod: number; // months
  status: 'active' | 'completed' | 'cancelled';
}

interface BurnRecord {
  id: string;
  amount: string;
  type: 'manual' | 'automatic' | 'gas_fee';
  timestamp: string;
  txHash: string;
  reason: string;
}

interface LockupRecord {
  id: string;
  holder: string;
  amount: string;
  lockDate: string;
  unlockDate: string;
  status: 'locked' | 'unlocked';
  type: 'manual' | 'vesting' | 'staking';
}

interface AirdropCampaign {
  id: string;
  name: string;
  totalAmount: string;
  recipients: number;
  status: 'pending' | 'active' | 'completed' | 'cancelled';
  startDate: string;
  endDate: string;
  vestingMonths?: number;
}

const VoltronTokenManagementPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(false);
  const [gasBurnRate, setGasBurnRate] = useState(3);
  const [tokenStats, setTokenStats] = useState<TokenStats>({
    totalSupply: VTTN_TOTAL_SUPPLY.toLocaleString(),
    circulatingSupply: VTTN_CIRCULATING.toLocaleString(),
    burnedAmount: VTTN_BURNED.toLocaleString(),
    lockedAmount: VTTN_LOCKED.toLocaleString(),
    vestingAmount: '0',
    currentPrice: VTTN_PRICE_USD,
    marketCap: VTTN_PRICE_USD * VTTN_CIRCULATING,
    holders: VTTN_HOLDERS
  });

  // 모달 상태
  const [showMintModal, setShowMintModal] = useState(false);
  const [showBurnModal, setShowBurnModal] = useState(false);
  const [showLockupModal, setShowLockupModal] = useState(false);
  const [showVestingModal, setShowVestingModal] = useState(false);
  const [showAirdropModal, setShowAirdropModal] = useState(false);
  const [showWalletModal, setShowWalletModal] = useState(false);

  // 폼 데이터
  const [mintForm, setMintForm] = useState({
    amount: '',
    recipient: '',
    reason: ''
  });

  const [burnForm, setBurnForm] = useState({
    amount: '',
    type: 'manual' as 'manual' | 'automatic',
    reason: ''
  });

  const [lockupForm, setLockupForm] = useState({
    holder: '',
    amount: '',
    duration: '',
    reason: ''
  });

  const [vestingForm, setVestingForm] = useState({
    beneficiary: '',
    amount: '',
    duration: '10',
    cliffPeriod: '0',
    startDate: ''
  });

  const [airdropForm, setAirdropForm] = useState({
    name: '',
    csvFile: null as File | null,
    totalAmount: '',
    vestingMonths: '0'
  });

  // 데이터 로드
  useEffect(() => {
    loadTokenData();
  }, []);

  const loadTokenData = async () => {
    setLoading(true);
    try {
      // Speed Wallet 백엔드에서 토큰 데이터 로드
      const [tokenResponse, usersResponse] = await Promise.all([
        fetch('http://localhost:3600/api/blockchain/token-stats', {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('voltron-jwt-token')}`
          }
        }),
        fetch('http://localhost:3600/api/admin/users?limit=1', {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('voltron-jwt-token')}`
          }
        })
      ]);
      
      if (tokenResponse.ok) {
        const tokenData = await tokenResponse.json();
        const updatedStats = { ...tokenData };
        let holderCount = VTTN_HOLDERS;

        if (usersResponse.ok) {
          const usersData = await usersResponse.json();
          if (usersData.pagination?.total) {
            holderCount = usersData.pagination.total;
            updatedStats.holders = holderCount;
          }
        }

        setTokenStats(prev => ({
          ...prev,
          ...updatedStats,
          totalSupply: VTTN_TOTAL_SUPPLY.toLocaleString(),
          circulatingSupply: VTTN_CIRCULATING.toLocaleString(),
          burnedAmount: VTTN_BURNED.toLocaleString(),
          lockedAmount: VTTN_LOCKED.toLocaleString(),
          holders: holderCount,
          marketCap: (updatedStats.currentPrice ?? VTTN_PRICE_USD) * VTTN_CIRCULATING,
        }));
      }
    } catch (error) {
      console.error('Failed to load token data:', error);
    } finally {
      setLoading(false);
    }
  };

  // 민팅 함수
  const handleMint = async () => {
    try {
      setLoading(true);
      // 스마트 컨트랙트 호출
      console.log('Minting tokens:', mintForm);
      setShowMintModal(false);
      setMintForm({ amount: '', recipient: '', reason: '' });
      await loadTokenData();
    } catch (error) {
      console.error('Minting failed:', error);
    } finally {
      setLoading(false);
    }
  };

  // 소각 함수
  const handleBurn = async () => {
    try {
      setLoading(true);
      console.log('Burning tokens:', burnForm);
      setShowBurnModal(false);
      setBurnForm({ amount: '', type: 'manual', reason: '' });
      await loadTokenData();
    } catch (error) {
      console.error('Burning failed:', error);
    } finally {
      setLoading(false);
    }
  };

  // 락업 함수
  const handleLockup = async () => {
    try {
      setLoading(true);
      console.log('Creating lockup:', lockupForm);
      setShowLockupModal(false);
      setLockupForm({ holder: '', amount: '', duration: '', reason: '' });
      await loadTokenData();
    } catch (error) {
      console.error('Lockup failed:', error);
    } finally {
      setLoading(false);
    }
  };

  // 베스팅 함수
  const handleVesting = async () => {
    try {
      setLoading(true);
      console.log('Creating vesting schedule:', vestingForm);
      setShowVestingModal(false);
      setVestingForm({ beneficiary: '', amount: '', duration: '10', cliffPeriod: '0', startDate: '' });
      await loadTokenData();
    } catch (error) {
      console.error('Vesting failed:', error);
    } finally {
      setLoading(false);
    }
  };

  // 에어드롭 함수
  const handleAirdrop = async () => {
    try {
      setLoading(true);
      
      // CSV 파일에서 수신자 목록 파싱 (Mock 데이터)
      const recipients = [
        { address: '0x742d35Cc6634C0532925a3b8D4C2C2C2C2C2C2C2', amount: '1000' },
        { address: '0x8ba1f109551bD432803012645Hac136c22C177', amount: '1500' },
        { address: '0x1234567890123456789012345678901234567890', amount: '2000' }
      ];
      
      // 실제 블록체인 서비스를 통한 에어드롭 실행
      const result = await blockchainService.executeAirdrop(
        'voltron',
        'VTTN',
        recipients,
        parseInt(airdropForm.vestingMonths) || undefined
      );
      
      if (result.success) {
        alert(`에어드롭이 성공적으로 실행되었습니다!\n수신자: ${result.totalRecipients}명\n총 금액: ${result.totalAmount} VTTN\n트랜잭션 해시: ${result.txHash}`);
      }
      
      console.log('Airdrop result:', result);
      setShowAirdropModal(false);
      setAirdropForm({ name: '', csvFile: null, totalAmount: '', vestingMonths: '0' });
      await loadTokenData();
    } catch (error) {
      console.error('Airdrop failed:', error);
      alert(`에어드롭 실행 중 오류가 발생했습니다: ${error instanceof Error ? error.message : String(error)}`);
    } finally {
      setLoading(false);
    }
  };

  // 사용되지 않는 함수들을 실제로 사용하도록 수정
  const handleQuickAction = (action: string) => {
    switch (action) {
      case 'vesting':
        handleVesting();
        break;
      case 'airdrop':
        handleAirdrop();
        break;
      default:
        break;
    }
  };

  const renderOverview = () => (
    <div className="space-y-6">
      {/* 토큰 통계 카드 */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">총 발행량</p>
              <p className="text-2xl font-bold text-gray-900">{tokenStats.totalSupply}</p>
              <p className="text-xs text-gray-500">VTTN</p>
            </div>
            <div className="p-2 bg-blue-100 rounded-lg">
              <Coins className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">유통량</p>
              <p className="text-2xl font-bold text-green-600">{tokenStats.circulatingSupply}</p>
              <p className="text-xs text-gray-500">VTTN</p>
            </div>
            <div className="p-2 bg-green-100 rounded-lg">
              <Activity className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">소각량</p>
              <p className="text-2xl font-bold text-red-600">{tokenStats.burnedAmount}</p>
              <p className="text-xs text-gray-500">VTTN</p>
            </div>
            <div className="p-2 bg-red-100 rounded-lg">
              <Flame className="w-6 h-6 text-red-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">락업량</p>
              <p className="text-2xl font-bold text-purple-600">{tokenStats.lockedAmount}</p>
              <p className="text-xs text-gray-500">VTTN</p>
            </div>
            <div className="p-2 bg-purple-100 rounded-lg">
              <Lock className="w-6 h-6 text-purple-600" />
            </div>
          </div>
        </div>
      </div>

      {/* 가격 및 시장 정보 */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-semibold text-gray-900">현재 가격</h3>
            <DollarSign className="w-5 h-5 text-green-500" />
          </div>
          <p className="text-3xl font-bold text-green-600">${tokenStats.currentPrice}</p>
          <p className="text-sm text-gray-500 mt-1">+5.67% (24h)</p>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-semibold text-gray-900">시가총액</h3>
            <BarChart3 className="w-5 h-5 text-blue-500" />
          </div>
          <p className="text-3xl font-bold text-blue-600">${(tokenStats.marketCap / 1000000).toFixed(1)}M</p>
          <p className="text-sm text-gray-500 mt-1">USD</p>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-semibold text-gray-900">홀더 수</h3>
            <Users className="w-5 h-5 text-purple-500" />
          </div>
          <p className="text-3xl font-bold text-purple-600">{tokenStats.holders.toLocaleString()}</p>
          <p className="text-sm text-gray-500 mt-1">+12 (24h)</p>
        </div>
      </div>

      {/* 빠른 액션 버튼 */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">빠른 액션</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <button
            onClick={() => setShowMintModal(true)}
            className="flex flex-col items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <Plus className="w-6 h-6 text-green-500 mb-2" />
            <span className="text-sm font-medium text-gray-700">민팅</span>
          </button>

          <button
            onClick={() => setShowBurnModal(true)}
            className="flex flex-col items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <Flame className="w-6 h-6 text-red-500 mb-2" />
            <span className="text-sm font-medium text-gray-700">소각</span>
          </button>

          <button
            onClick={() => setShowLockupModal(true)}
            className="flex flex-col items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <Lock className="w-6 h-6 text-purple-500 mb-2" />
            <span className="text-sm font-medium text-gray-700">락업</span>
          </button>

          <button
            onClick={() => handleQuickAction('vesting')}
            className="flex flex-col items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <Calendar className="w-6 h-6 text-blue-500 mb-2" />
            <span className="text-sm font-medium text-gray-700">베스팅</span>
          </button>

          <button
            onClick={() => handleQuickAction('airdrop')}
            className="flex flex-col items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <Send className="w-6 h-6 text-orange-500 mb-2" />
            <span className="text-sm font-medium text-gray-700">에어드롭</span>
          </button>

          <button
            onClick={() => setShowWalletModal(true)}
            className="flex flex-col items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <Wallet className="w-6 h-6 text-indigo-500 mb-2" />
            <span className="text-sm font-medium text-gray-700">지갑관리</span>
          </button>
        </div>
      </div>
    </div>
  );

  const renderMintingTab = () => (
    <div className="space-y-6">
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">토큰 민팅</h3>
          <button
            onClick={() => setShowMintModal(true)}
            className="flex items-center space-x-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600"
          >
            <Plus className="w-4 h-4" />
            <span>새 민팅</span>
          </button>
        </div>
        
        {/* 민팅 설정 */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">민팅 권한</label>
              <div className="flex items-center space-x-4">
                <label className="flex items-center">
                  <input type="checkbox" className="mr-2" defaultChecked />
                  <span className="text-sm text-gray-600">관리자 민팅 활성화</span>
                </label>
                <label className="flex items-center">
                  <input type="checkbox" className="mr-2" />
                  <span className="text-sm text-gray-600">자동 민팅 활성화</span>
                </label>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">일일 민팅 한도</label>
              <input
                type="text"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                placeholder="1,000,000 VTTN"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">최대 총 공급량</label>
              <input
                type="text"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                placeholder="10,000,000,000 VTTN"
              />
            </div>
          </div>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">민팅 수수료</label>
              <input
                type="text"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                placeholder="0.1%"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">승인 필요</label>
              <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500">
                <option>다중 서명 필요</option>
                <option>단일 관리자 승인</option>
                <option>자동 승인</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">민팅 지연</label>
              <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500">
                <option>즉시 실행</option>
                <option>24시간 지연</option>
                <option>48시간 지연</option>
                <option>7일 지연</option>
              </select>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderBurningTab = () => (
    <div className="space-y-6">
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">토큰 소각</h3>
          <button
            onClick={() => setShowBurnModal(true)}
            className="flex items-center space-x-2 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
          >
            <Flame className="w-4 h-4" />
            <span>수동 소각</span>
          </button>
        </div>
        
        {/* 소각 설정 */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">자동 소각 설정</label>
              <div className="space-y-2">
                <label className="flex items-center">
                  <input type="checkbox" className="mr-2" defaultChecked />
                  <span className="text-sm text-gray-600">가스비 소각 (1-5%)</span>
                </label>
                <label className="flex items-center">
                  <input type="checkbox" className="mr-2" defaultChecked />
                  <span className="text-sm text-gray-600">블록 보상 소각 (15%)</span>
                </label>
                <label className="flex items-center">
                  <input type="checkbox" className="mr-2" defaultChecked />
                  <span className="text-sm text-gray-600">연간 소각 (15%)</span>
                </label>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">가스비 소각 비율</label>
              <div className="flex items-center space-x-2">
                <input
                  type="range"
                  min="1"
                  max="5"
                  value={gasBurnRate}
                  onChange={(e) => setGasBurnRate(parseInt(e.target.value))}
                  className="flex-1"
                />
                <span className="text-sm font-medium text-gray-900">{gasBurnRate}%</span>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">소각 스케줄</label>
              <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500">
                <option>매일</option>
                <option>매주</option>
                <option>매월</option>
                <option>분기별</option>
              </select>
            </div>
          </div>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">할빙 설정</label>
              <div className="space-y-2">
                <label className="flex items-center">
                  <input type="checkbox" className="mr-2" defaultChecked />
                  <span className="text-sm text-gray-600">2년마다 할빙 활성화</span>
                </label>
                <div className="ml-6">
                  <label className="block text-xs text-gray-500 mb-1">다음 할빙까지</label>
                  <p className="text-sm font-medium text-gray-900">547일 (블록 #1,234,567)</p>
                </div>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">소각 주소</label>
              <input
                type="text"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                value="0x000000000000000000000000000000000000dEaD"
                readOnly
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">소각 알림</label>
              <div className="space-y-2">
                <label className="flex items-center">
                  <input type="checkbox" className="mr-2" defaultChecked />
                  <span className="text-sm text-gray-600">대량 소각 시 알림</span>
                </label>
                <label className="flex items-center">
                  <input type="checkbox" className="mr-2" />
                  <span className="text-sm text-gray-600">일일 소각 리포트</span>
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderLockupTab = () => (
    <div className="space-y-6">
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">락업 관리</h3>
          <button
            onClick={() => setShowLockupModal(true)}
            className="flex items-center space-x-2 px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600"
          >
            <Lock className="w-4 h-4" />
            <span>새 락업</span>
          </button>
        </div>
        
        {/* 락업 통계 */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-purple-50 border border-purple-200 p-4 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-purple-600">총 락업량</p>
                <p className="text-2xl font-bold text-purple-900">{tokenStats.lockedAmount}</p>
              </div>
              <Lock className="w-8 h-8 text-purple-500" />
            </div>
          </div>
          
          <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-blue-600">활성 락업</p>
                <p className="text-2xl font-bold text-blue-900">1,234</p>
              </div>
              <Shield className="w-8 h-8 text-blue-500" />
            </div>
          </div>
          
          <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-green-600">이번 달 해제</p>
                <p className="text-2xl font-bold text-green-900">5,678,900</p>
              </div>
              <Unlock className="w-8 h-8 text-green-500" />
            </div>
          </div>
        </div>
        
        {/* 락업 설정 */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">락업 유형</label>
              <div className="space-y-2">
                <label className="flex items-center">
                  <input type="radio" name="lockupType" className="mr-2" defaultChecked />
                  <span className="text-sm text-gray-600">고정 기간 락업</span>
                </label>
                <label className="flex items-center">
                  <input type="radio" name="lockupType" className="mr-2" />
                  <span className="text-sm text-gray-600">조건부 락업</span>
                </label>
                <label className="flex items-center">
                  <input type="radio" name="lockupType" className="mr-2" />
                  <span className="text-sm text-gray-600">베스팅 락업</span>
                </label>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">최소 락업 기간</label>
              <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500">
                <option>1개월</option>
                <option>3개월</option>
                <option>6개월</option>
                <option>12개월</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">조기 해제 수수료</label>
              <input
                type="text"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                placeholder="10%"
              />
            </div>
          </div>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">락업 보상</label>
              <div className="space-y-2">
                <label className="flex items-center">
                  <input type="checkbox" className="mr-2" defaultChecked />
                  <span className="text-sm text-gray-600">락업 기간별 보상 지급</span>
                </label>
                <div className="ml-6 space-y-1">
                  <p className="text-xs text-gray-500">3개월: 2% APY</p>
                  <p className="text-xs text-gray-500">6개월: 5% APY</p>
                  <p className="text-xs text-gray-500">12개월: 10% APY</p>
                </div>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">자동 재락업</label>
              <div className="space-y-2">
                <label className="flex items-center">
                  <input type="checkbox" className="mr-2" />
                  <span className="text-sm text-gray-600">만료 시 자동 재락업</span>
                </label>
                <label className="flex items-center">
                  <input type="checkbox" className="mr-2" />
                  <span className="text-sm text-gray-600">보상 자동 재투자</span>
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderVestingTab = () => (
    <div className="space-y-6">
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">베스팅 관리</h3>
          <button
            onClick={() => setShowVestingModal(true)}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
          >
            <Calendar className="w-4 h-4" />
            <span>새 베스팅</span>
          </button>
        </div>
        
        {/* 베스팅 통계 */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-blue-600">총 베스팅</p>
                <p className="text-xl font-bold text-blue-900">{tokenStats.vestingAmount}</p>
              </div>
              <Calendar className="w-6 h-6 text-blue-500" />
            </div>
          </div>
          
          <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-green-600">활성 스케줄</p>
                <p className="text-xl font-bold text-green-900">156</p>
              </div>
              <CheckCircle className="w-6 h-6 text-green-500" />
            </div>
          </div>
          
          <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-yellow-600">이번 달 해제</p>
                <p className="text-xl font-bold text-yellow-900">2,345,678</p>
              </div>
              <Clock className="w-6 h-6 text-yellow-500" />
            </div>
          </div>
          
          <div className="bg-purple-50 border border-purple-200 p-4 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-purple-600">완료된 베스팅</p>
                <p className="text-xl font-bold text-purple-900">89</p>
              </div>
              <Target className="w-6 h-6 text-purple-500" />
            </div>
          </div>
        </div>
        
        {/* 베스팅 설정 */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">기본 베스팅 기간</label>
              <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500">
                <option>6개월</option>
                <option>10개월</option>
                <option>12개월</option>
                <option>24개월</option>
                <option>36개월</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">클리프 기간</label>
              <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500">
                <option>없음</option>
                <option>1개월</option>
                <option>3개월</option>
                <option>6개월</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">해제 주기</label>
              <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500">
                <option>매월</option>
                <option>분기별</option>
                <option>반기별</option>
                <option>연간</option>
              </select>
            </div>
          </div>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">베스팅 유형</label>
              <div className="space-y-2">
                <label className="flex items-center">
                  <input type="radio" name="vestingType" className="mr-2" defaultChecked />
                  <span className="text-sm text-gray-600">선형 베스팅</span>
                </label>
                <label className="flex items-center">
                  <input type="radio" name="vestingType" className="mr-2" />
                  <span className="text-sm text-gray-600">단계별 베스팅</span>
                </label>
                <label className="flex items-center">
                  <input type="radio" name="vestingType" className="mr-2" />
                  <span className="text-sm text-gray-600">성과 기반 베스팅</span>
                </label>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">조기 해제</label>
              <div className="space-y-2">
                <label className="flex items-center">
                  <input type="checkbox" className="mr-2" />
                  <span className="text-sm text-gray-600">조기 해제 허용</span>
                </label>
                <label className="flex items-center">
                  <input type="checkbox" className="mr-2" />
                  <span className="text-sm text-gray-600">수수료 적용 (20%)</span>
                </label>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">자동 알림</label>
              <div className="space-y-2">
                <label className="flex items-center">
                  <input type="checkbox" className="mr-2" defaultChecked />
                  <span className="text-sm text-gray-600">해제 7일 전 알림</span>
                </label>
                <label className="flex items-center">
                  <input type="checkbox" className="mr-2" defaultChecked />
                  <span className="text-sm text-gray-600">해제 당일 알림</span>
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderAirdropTab = () => (
    <div className="space-y-6">
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">에어드롭 관리</h3>
          <button
            onClick={() => setShowAirdropModal(true)}
            className="flex items-center space-x-2 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600"
          >
            <Send className="w-4 h-4" />
            <span>새 에어드롭</span>
          </button>
        </div>
        
        {/* 에어드롭 통계 */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-orange-50 border border-orange-200 p-4 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-orange-600">총 에어드롭</p>
                <p className="text-xl font-bold text-orange-900">{VTTN_CIRCULATING.toLocaleString()}</p>
              </div>
              <Send className="w-6 h-6 text-orange-500" />
            </div>
          </div>
          
          <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-green-600">활성 캠페인</p>
                <p className="text-xl font-bold text-green-900">3</p>
              </div>
              <Activity className="w-6 h-6 text-green-500" />
            </div>
          </div>
          
          <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-blue-600">총 수혜자</p>
                <p className="text-xl font-bold text-blue-900">12,456</p>
              </div>
              <Users className="w-6 h-6 text-blue-500" />
            </div>
          </div>
          
          <div className="bg-purple-50 border border-purple-200 p-4 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-purple-600">완료된 캠페인</p>
                <p className="text-xl font-bold text-purple-900">15</p>
              </div>
              <CheckCircle className="w-6 h-6 text-purple-500" />
            </div>
          </div>
        </div>
        
        {/* 에어드롭 설정 */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">에어드롭 대상 선택</label>
              <div className="space-y-2">
                <label className="flex items-center">
                  <input type="radio" name="airdropTarget" className="mr-2" defaultChecked />
                  <span className="text-sm text-gray-600">전체 회원 (코드, 지갑, 닉네임 기준)</span>
                </label>
                <label className="flex items-center">
                  <input type="radio" name="airdropTarget" className="mr-2" />
                  <span className="text-sm text-gray-600">특정 조건 회원</span>
                </label>
                <label className="flex items-center">
                  <input type="radio" name="airdropTarget" className="mr-2" />
                  <span className="text-sm text-gray-600">CSV 파일 업로드</span>
                </label>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">에어드롭 유형</label>
              <div className="space-y-2">
                <label className="flex items-center">
                  <input type="radio" name="airdropType" className="mr-2" defaultChecked />
                  <span className="text-sm text-gray-600">웰컴토큰 (추천인 시스템)</span>
                </label>
                <label className="flex items-center">
                  <input type="radio" name="airdropType" className="mr-2" />
                  <span className="text-sm text-gray-600">즉시 지급</span>
                </label>
                <label className="flex items-center">
                  <input type="radio" name="airdropType" className="mr-2" />
                  <span className="text-sm text-gray-600">베스팅 에어드롭</span>
                </label>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">웰컴토큰 설정</label>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">신규 회원</label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    placeholder="100 VTTN"
                    defaultValue="100"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">추천인</label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    placeholder="100 VTTN"
                    defaultValue="100"
                  />
                </div>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">최대 수혜자 수</label>
              <input
                type="text"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                placeholder="전체 회원 (제한 없음)"
              />
            </div>
          </div>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">자격 조건</label>
              <div className="space-y-2">
                <label className="flex items-center">
                  <input type="checkbox" className="mr-2" defaultChecked />
                  <span className="text-sm text-gray-600">최소 보유량 (100 VTTN)</span>
                </label>
                <label className="flex items-center">
                  <input type="checkbox" className="mr-2" />
                  <span className="text-sm text-gray-600">스테이킹 참여자</span>
                </label>
                <label className="flex items-center">
                  <input type="checkbox" className="mr-2" />
                  <span className="text-sm text-gray-600">거래 활동 (30일)</span>
                </label>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">중복 방지</label>
              <div className="space-y-2">
                <label className="flex items-center">
                  <input type="checkbox" className="mr-2" defaultChecked />
                  <span className="text-sm text-gray-600">지갑 주소 중복 체크</span>
                </label>
                <label className="flex items-center">
                  <input type="checkbox" className="mr-2" defaultChecked />
                  <span className="text-sm text-gray-600">IP 주소 중복 체크</span>
                </label>
                <label className="flex items-center">
                  <input type="checkbox" className="mr-2" />
                  <span className="text-sm text-gray-600">KYC 인증 필요</span>
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderWalletTab = () => (
    <div className="space-y-6">
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">지갑 관리</h3>
          <button
            onClick={() => setShowWalletModal(true)}
            className="flex items-center space-x-2 px-4 py-2 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600"
          >
            <Wallet className="w-4 h-4" />
            <span>지갑 추가</span>
          </button>
        </div>
        
        {/* 지갑 통계 */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-indigo-50 border border-indigo-200 p-4 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-indigo-600">총 홀더</p>
                <p className="text-xl font-bold text-indigo-900">{tokenStats.holders.toLocaleString()}</p>
              </div>
              <Users className="w-6 h-6 text-indigo-500" />
            </div>
          </div>
          
          <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-green-600">활성 지갑</p>
                <p className="text-xl font-bold text-green-900">7,234</p>
              </div>
              <Activity className="w-6 h-6 text-green-500" />
            </div>
          </div>
          
          <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-yellow-600">대형 홀더</p>
                    <p className="text-xl font-bold text-yellow-900">156</p>
                  </div>
                  <Star className="w-6 h-6 text-yellow-500" />
                </div>
              </div>
          
          <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-red-600">제재 지갑</p>
                <p className="text-xl font-bold text-red-900">23</p>
              </div>
              <Shield className="w-6 h-6 text-red-500" />
            </div>
          </div>
        </div>
        
        {/* 지갑 관리 도구 */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">지갑 검색</label>
              <input
                type="text"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                placeholder="지갑 주소 또는 ENS 이름"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">필터</label>
              <div className="space-y-2">
                <label className="flex items-center">
                  <input type="checkbox" className="mr-2" />
                  <span className="text-sm text-gray-600">대형 홀더 (&gt;100K VTTN)</span>
                </label>
                <label className="flex items-center">
                  <input type="checkbox" className="mr-2" />
                  <span className="text-sm text-gray-600">스테이킹 참여자</span>
                </label>
                <label className="flex items-center">
                  <input type="checkbox" className="mr-2" />
                  <span className="text-sm text-gray-600">락업 보유자</span>
                </label>
                <label className="flex items-center">
                  <input type="checkbox" className="mr-2" />
                  <span className="text-sm text-gray-600">제재 대상</span>
                </label>
              </div>
            </div>
          </div>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">일괄 작업</label>
              <div className="space-y-2">
                <button className="w-full px-3 py-2 text-left border border-gray-300 rounded-lg hover:bg-gray-50">
                  <div className="flex items-center space-x-2">
                    <Send className="w-4 h-4 text-blue-500" />
                    <span className="text-sm text-gray-700">일괄 전송</span>
                  </div>
                </button>
                <button className="w-full px-3 py-2 text-left border border-gray-300 rounded-lg hover:bg-gray-50">
                  <div className="flex items-center space-x-2">
                    <Lock className="w-4 h-4 text-purple-500" />
                    <span className="text-sm text-gray-700">일괄 락업</span>
                  </div>
                </button>
                <button className="w-full px-3 py-2 text-left border border-gray-300 rounded-lg hover:bg-gray-50">
                  <div className="flex items-center space-x-2">
                    <Shield className="w-4 h-4 text-red-500" />
                    <span className="text-sm text-gray-700">일괄 제재</span>
                  </div>
                </button>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">내보내기</label>
              <div className="space-y-2">
                <button className="w-full px-3 py-2 text-left border border-gray-300 rounded-lg hover:bg-gray-50">
                  <div className="flex items-center space-x-2">
                    <Download className="w-4 h-4 text-green-500" />
                    <span className="text-sm text-gray-700">홀더 목록 CSV</span>
                  </div>
                </button>
                <button className="w-full px-3 py-2 text-left border border-gray-300 rounded-lg hover:bg-gray-50">
                  <div className="flex items-center space-x-2">
                    <FileText className="w-4 h-4 text-blue-500" />
                    <span className="text-sm text-gray-700">거래 내역 리포트</span>
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderGasTab = () => (
    <div className="space-y-6">
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">가스비 관리</h3>
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-500">실시간 가스비:</span>
            <span className="text-lg font-bold text-orange-600">25 Gwei</span>
          </div>
        </div>
        
        {/* 가스비 통계 */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-orange-50 border border-orange-200 p-4 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-orange-600">일일 가스비 수익</p>
                <p className="text-xl font-bold text-orange-900">1,234 VTTN</p>
              </div>
              <Fuel className="w-6 h-6 text-orange-500" />
            </div>
          </div>
          
          <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-red-600">일일 소각량</p>
                <p className="text-xl font-bold text-red-900">37 VTTN</p>
              </div>
              <Flame className="w-6 h-6 text-red-500" />
            </div>
          </div>
          
          <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-blue-600">가스비 풀</p>
                <p className="text-xl font-bold text-blue-900">45,678 VTTN</p>
              </div>
              <Database className="w-6 h-6 text-blue-500" />
            </div>
          </div>
          
          <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-green-600">노드 분배</p>
                <p className="text-xl font-bold text-green-900">1,197 VTTN</p>
              </div>
              <Target className="w-6 h-6 text-green-500" />
            </div>
          </div>
        </div>
        
        {/* 가스비 설정 */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">기본 가스비</label>
              <div className="flex items-center space-x-2">
                <input
                  type="text"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  placeholder="25"
                />
                <span className="text-sm text-gray-500">Gwei</span>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">가스비 소각 비율</label>
              <div className="flex items-center space-x-2">
                <input
                  type="range"
                  min="1"
                  max="5"
                  defaultValue="3"
                  className="flex-1"
                />
                <span className="text-sm font-medium text-gray-900">3%</span>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">동적 조정</label>
              <div className="space-y-2">
                <label className="flex items-center">
                  <input type="checkbox" className="mr-2" defaultChecked />
                  <span className="text-sm text-gray-600">네트워크 혼잡도 기반 조정</span>
                </label>
                <label className="flex items-center">
                  <input type="checkbox" className="mr-2" />
                  <span className="text-sm text-gray-600">시간대별 조정</span>
                </label>
              </div>
            </div>
          </div>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">가스비 분배</label>
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">검증자:</span>
                  <span className="text-sm font-medium text-gray-900">70%</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">소각:</span>
                  <span className="text-sm font-medium text-gray-900">15%</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">개발 기금:</span>
                  <span className="text-sm font-medium text-gray-900">10%</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">커뮤니티:</span>
                  <span className="text-sm font-medium text-gray-900">5%</span>
                </div>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">가스비 한도</label>
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-gray-600">최소:</span>
                  <input
                    type="text"
                    className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm"
                    placeholder="1"
                  />
                  <span className="text-sm text-gray-500">Gwei</span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-gray-600">최대:</span>
                  <input
                    type="text"
                    className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm"
                    placeholder="1000"
                  />
                  <span className="text-sm text-gray-500">Gwei</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // 모달 컴포넌트들
  const MintModal: React.FC = () => {
    if (!showMintModal) return null;
    
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg w-full max-w-md mx-4">
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">토큰 민팅</h3>
              <button
                onClick={() => setShowMintModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">수령자 주소</label>
                <input
                  type="text"
                  value={mintForm.recipient}
                  onChange={(e) => setMintForm({...mintForm, recipient: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  placeholder="0x..."
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">수량</label>
                <input
                  type="text"
                  value={mintForm.amount}
                  onChange={(e) => setMintForm({...mintForm, amount: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  placeholder="1000000"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">사유</label>
                <textarea
                  value={mintForm.reason}
                  onChange={(e) => setMintForm({...mintForm, reason: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  rows={3}
                  placeholder="민팅 사유를 입력하세요"
                />
              </div>
            </div>
            
            <div className="flex space-x-3 mt-6">
              <button
                onClick={() => setShowMintModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                취소
              </button>
              <button
                onClick={handleMint}
                disabled={loading}
                className="flex-1 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50"
              >
                {loading ? '처리 중...' : '민팅 실행'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const BurnModal: React.FC = () => {
    if (!showBurnModal) return null;
    
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg w-full max-w-md mx-4">
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">토큰 소각</h3>
              <button
                onClick={() => setShowBurnModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">소각 수량</label>
                <input
                  type="text"
                  value={burnForm.amount}
                  onChange={(e) => setBurnForm({...burnForm, amount: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  placeholder="1000000"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">소각 유형</label>
                <select
                  value={burnForm.type}
                  onChange={(e) => setBurnForm({...burnForm, type: e.target.value as 'manual' | 'automatic'})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                >
                  <option value="manual">수동 소각</option>
                  <option value="automatic">자동 소각</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">사유</label>
                <textarea
                  value={burnForm.reason}
                  onChange={(e) => setBurnForm({...burnForm, reason: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  rows={3}
                  placeholder="소각 사유를 입력하세요"
                />
              </div>
            </div>
            
            <div className="flex space-x-3 mt-6">
              <button
                onClick={() => setShowBurnModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                취소
              </button>
              <button
                onClick={handleBurn}
                disabled={loading}
                className="flex-1 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:opacity-50"
              >
                {loading ? '처리 중...' : '소각 실행'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Voltron 토큰 관리</h1>
          <p className="text-gray-600 mt-1">VTTN 토큰의 모든 기능을 통합 관리하세요</p>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={loadTokenData}
            className="flex items-center space-x-2 px-3 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
          >
            <RefreshCw className="w-4 h-4" />
            <span>새로고침</span>
          </button>
        </div>
      </div>

      {/* 탭 네비게이션 */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {[
            { id: 'overview', name: '개요', icon: BarChart3 },
            { id: 'minting', name: '민팅', icon: Plus },
            { id: 'burning', name: '소각', icon: Flame },
            { id: 'lockup', name: '락업', icon: Lock },
            { id: 'vesting', name: '베스팅', icon: Calendar },
            { id: 'airdrop', name: '에어드롭', icon: Send },
            { id: 'wallet', name: '지갑관리', icon: Wallet },
            { id: 'gas', name: '가스비', icon: Fuel }
          ].map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-orange-500 text-orange-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Icon className="w-4 h-4" />
                <span>{tab.name}</span>
              </button>
            );
          })}
        </nav>
      </div>

      {/* 탭 콘텐츠 */}
      {activeTab === 'overview' && renderOverview()}
      {activeTab === 'minting' && renderMintingTab()}
      {activeTab === 'burning' && renderBurningTab()}
      {activeTab === 'lockup' && renderLockupTab()}
      {activeTab === 'vesting' && renderVestingTab()}
      {activeTab === 'airdrop' && renderAirdropTab()}
      {activeTab === 'wallet' && renderWalletTab()}
      {activeTab === 'gas' && renderGasTab()}

      {/* 모달들 */}
      <MintModal />
      <BurnModal />
    </div>
  );
};

export default VoltronTokenManagementPage;